import { privateRequest, type ApiResponse } from "../axios";

export interface TerritoryResponse {
  TotalRecords: number;
  Territory: Territory[];
}

export interface Territory {
  TerritoryId: number;
  TerritoryName: string;
  ParentTerritoryId: number | string;
  ParentTerritoryName: string;
  CreatedDate:string;
  ModifiedDate:string;
  CreatedBy: string;
  ModifiedBy: string;
}

export interface CreateTerritoryPayload {
  Id?: number | undefined;
  Name: string;
  ParentTerritoryId: number;
}

export interface ViewTerritory {
  TerritoryName: string;
  ParentTerritoryName: string;
  Status: string;
  Message: string;
}

export interface ViewReportResponse {
  TotalRecords: number;
  Items: ViewTerritory[];
}

export interface ViewReportPrams {
  Id: number | string;
  from: number;
  text: string;
  to: number;
}

const ENDPOINTS = {
  TERRITORIES: "api/CRM/Territories",
  PARENT_TERRITORIES: "/api/CRM/ChildTerritories",
  TERRITORY: "/api/CRM/Territory",
  EDIT_TERRITORY: (id: number) => `/api/CRM/Territory/${id}`,
  EXCEL_EXPORT: "api/CRM/Territory/Export",
  BULK_UPLOAD: "/api/CRM/TerritoryBulkUpload",
  TERRITORY_REPORT: "api/CRM/Territory/BulkUpload/FileName",
  EXPORT_BULK_UPLOAD_REPORT: (id: number | string) =>
    `/api/CRM/Territory/BulkUpload/ExportReport/${id}`,
  EXPORT_BULK_UPLOAD_FILE: (id: number | string) =>
    `/api/CRM/Territory/BulkUpload/ExportFile/${id}`,
  VIEW_TERRITORIES: `/api/CRM/Territory/BulkUpload/viewReport`,
};

const getTerritories = async (
  params?: any,
): Promise<ApiResponse<TerritoryResponse>> => {
  const response = await privateRequest.get<ApiResponse<TerritoryResponse>>(
    ENDPOINTS.TERRITORIES,
    {
      params,
    },
  );

  return response?.data;
};

const getParentTerritories = async (
  params?: any,
): Promise<ApiResponse<Territory[]>> => {
  const response = await privateRequest.get<ApiResponse<Territory[]>>(
    ENDPOINTS.PARENT_TERRITORIES,
    {
      params,
    },
  );

  return response?.data;
};

const addTerritory = async (
  payload?: CreateTerritoryPayload,
): Promise<ApiResponse<Territory[]>> => {
  const response = await privateRequest.post<ApiResponse<Territory[]>>(
    ENDPOINTS.TERRITORY,
    payload,
  );

  return response?.data;
};

const updateTerritory = async (
  payload?: CreateTerritoryPayload,
): Promise<ApiResponse<Territory[]>> => {
  const response = await privateRequest.put<ApiResponse<Territory[]>>(
    ENDPOINTS.EDIT_TERRITORY(payload?.Id as number),
    payload,
  );

  return response?.data;
};

const deleteTerritory = async (id?: number): Promise<ApiResponse<any>> => {
  const response = await privateRequest.delete<ApiResponse<any>>(
    ENDPOINTS.EDIT_TERRITORY(id as number),
  );

  return response?.data;
};

const downloadExcelFile = async () => {
  const response = await privateRequest.get(ENDPOINTS.EXCEL_EXPORT, {
    responseType: "arraybuffer",
  });
  return response;
};

const bulkUploadTerritory = async (
  payload?: any | undefined,
): Promise<ApiResponse<any>> => {
  const response = await privateRequest.post<ApiResponse<any>>(
    ENDPOINTS.BULK_UPLOAD,
    payload,
  );
  return response?.data;
};

const fetchTerritoryReport = async (payload: any) => {
  const response = await privateRequest.get(ENDPOINTS.TERRITORY_REPORT, {
    params: payload,
  });
  return response.data;
};

const exportBulkUploadReport = async (fileId: string) => {
  const response = await privateRequest.post(
    ENDPOINTS.EXPORT_BULK_UPLOAD_REPORT(fileId),
    {},
    {
      responseType: "arraybuffer",
    },
  );
  return response;
};

const exportBulkUploadFile = async (fileId: string) => {
  const response = await privateRequest.post(
    ENDPOINTS.EXPORT_BULK_UPLOAD_FILE(fileId),
    {},
    {
      responseType: "arraybuffer",
    },
  );
  return response;
};

const getViewTerritoryReport = async (params: ViewReportPrams) => {
  const response = await privateRequest.get<ViewReportResponse>(
    ENDPOINTS.VIEW_TERRITORIES,
    {
      params,
    },
  );
  return response.data;
};

const TerritoryService = {
  getTerritories,
  getParentTerritories,
  addTerritory,
  updateTerritory,
  deleteTerritory,
  downloadExcelFile,
  bulkUploadTerritory,
  fetchTerritoryReport,
  exportBulkUploadReport,
  exportBulkUploadFile,
  getViewTerritoryReport,
};

export default TerritoryService;
